import { autoDarkColors } from "@/modules/theme/color/darkModeColor.ts";
import type { ThemeProps, ThemeVarGetters, ThemeVariant } from "@/modules/theme/types.ts";

import png from "@/styles/vdc/apple-touch-icon.png?url";
import icon from "@/styles/vdc/favicon.ico?url";
import favicon from "@/styles/vdc/favicon.svg?url";
import loginIntroBg from "@/styles/vdc/login-bg.jpg?url";
import logoDark from "@/styles/vdc/logo-dark.svg?url";
import loginLogo from "@/styles/vdc/logo.svg";
import logo from "@/styles/vdc/logo.svg?url";

export function makeTheme(variant: ThemeVariant) {
    const { isDark } = variant;

    const baseColors: ThemeVarGetters = autoDarkColors({
        "color-primary": "#f556a8",
        "color-secondary": "#e5e5e5",
    });

    return {
        ...baseColors,

        "logo-image": isDark ? `url("${logoDark}")` : `url("${logo}")`,

        "header-logo-width": "56px",
        "header-logo-height": "22px",

        "button-primary-bg": "linear-gradient(90deg, rgba(245, 86, 169,1) 0%, rgba(186, 91, 170,1) 100%)",
        "icon-radio-primary-bg": "linear-gradient(90deg, rgba(245, 86, 169,1) 0%, rgba(186, 91, 170,1) 100%)",
        "button-new-bg": "linear-gradient(90deg, rgba(245, 86, 169,1) 0%, rgba(186, 91, 170,1) 100%)",

        "sidenav-item-text-color": isDark ? "#FFFFFF" : "var(--color-text)",
        "sidenav-item-before-display": "none",
        "sidenav-item-hover-bg-color": "var(--color-primary)",
        "sidenav-item-hover-icon-color": "#FFFFFF",
        "sidenav-item-hover-text-color": "#FFFFFF",
        "sidenav-item-active-bg-color": "var(--button-primary-bg)",
        "sidenav-item-active-icon-color": "#FFFFFF",
        "sidenav-item-active-text-color": "#FFFFFF",
        "sidenav-item-active-before-display": "none",

        //#region Login
        "login-body-bg-color": "var(--color-text)",
        "login-body-text-color": "var(--color-white)",
        "login-justify-content": "space-between",
        "login-align-items": "stretch",

        "login-intro-display": "flex",
        "login-intro-bg-image": `url("${loginIntroBg}")`,
        "login-intro-bg-size": "cover",
        "login-intro-bg-position": "50% 0",
        "login-intro-text-align": "left",
        "login-intro-flex-direction": "column",
        "login-intro-justify-content": "flex-end",
        "login-intro-align-items": "flex-start",
        "login-intro-padding": "60px",
        "login-title-font-size": "28px",
        "login-title-text-color": "",
        "login-title-font-weight": 600,
        "login-title-line-height": 1,
        "login-title-max-width": "400px",
        "login-title-padding": "0 0 16px 0",
        "login-subtitle-font-size": "16px",
        "login-subtitle-max-width": "400px",

        "login-form-title-font-size": "20px",
        "login-form-bg-color": "var(--color-white)",
        "login-form-text-color": "var(--color-text)",
        "login-form-input-border-color": "#dbe2e9",
        "login-form-button-border-radius": "var(--input-border-radius)",
        "login-form-or-text-color": "#dbe2e9",
        "login-form-or-border-color": "#dbe2e9",
        "login-form-google-text-color": "var(--color-text)",
        "login-form-google-bg-color": "var(--color-white)",
        "login-form-more-text-color": "var(--color-primary)",
        //#endregion
    } satisfies ThemeVarGetters;
}

export const props: ThemeProps = {
    favicon,
    icon,
    png,
    loginIntro: {
        style: {
            order: 2,
        },
    },
    loginForm: {
        style: {
            order: 1,
            flex: 1,
            justifyContent: "center",
        },
    },
    loginFormInner: {
        style: {
            maxWidth: "360px",
        },
    },
    loginLogoImg: {
        src: loginLogo,
        style: {
            maxWidth: "180px",
        },
    },
    loginGoogleButton: {
        icon: "",
        style: {
            justifyContent: "center",
            backgroundPosition: "16px center",
            backgroundRepeat: "no-repeat",
            borderColor: "var(--login-form-input-border-color)",
            backgroundImage:
                "url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGcgY2xpcC1wYXRoPSJ1cmwoI2NsaXAwKSI+CjxwYXRoIGQ9Ik01LjI2NTk5IDkuNzY1QzUuNzMzODcgOC4zNDk0NSA2LjYzNjg1IDcuMTE3ODEgNy44NDYxMSA2LjI0NThDOS4wNTUzNiA1LjM3Mzc4IDEwLjUwOTEgNC45MDU5NCAxMiA0LjkwOUMxMy42OSA0LjkwOSAxNS4yMTggNS41MDkgMTYuNDE4IDYuNDkxTDE5LjkxIDNDMTcuNzgyIDEuMTQ1IDE1LjA1NSAwIDEyIDBDNy4yNjk5OSAwIDMuMTk3OTkgMi42OTggMS4yMzk5OSA2LjY1TDUuMjY1OTkgOS43NjVaIiBmaWxsPSIjRUE0MzM1Ii8+CjxwYXRoIGQ9Ik0xNi4wNDAxIDE4LjAxM0MxNC45NTAxIDE4LjcxNiAxMy41NjYxIDE5LjA5MSAxMi4wMDAxIDE5LjA5MUMxMC41MTUxIDE5LjA5NCA5LjA2Njg2IDE4LjYyOTkgNy44NjAzIDE3Ljc2NDRDNi42NTM3MyAxNi44OTg4IDUuNzUgMTUuNjc1NiA1LjI3NzA2IDE0LjI2OEwxLjIzNzA2IDE3LjMzNUMyLjIyODMxIDE5LjM0MTMgMy43NjIzMyAyMS4wMjk0IDUuNjY0ODggMjIuMjA3NUM3LjU2NzQ0IDIzLjM4NTcgOS43NjIyNyAyNC4wMDY3IDEyLjAwMDEgMjRDMTQuOTMzMSAyNCAxNy43MzUxIDIyLjk1NyAxOS44MzQxIDIxTDE2LjA0MTEgMTguMDEzSDE2LjA0MDFaIiBmaWxsPSIjMzRBODUzIi8+CjxwYXRoIGQ9Ik0xOS44MzQgMjFDMjIuMDI5IDE4Ljk1MiAyMy40NTQgMTUuOTA0IDIzLjQ1NCAxMkMyMy40NTQgMTEuMjkgMjMuMzQ1IDEwLjUyNyAyMy4xODIgOS44MThIMTJWMTQuNDU1SDE4LjQzNkMxOC4xMTkgMTYuMDE0IDE3LjI2NiAxNy4yMjEgMTYuMDQxIDE4LjAxM0wxOS44MzQgMjFaIiBmaWxsPSIjNEE5MEUyIi8+CjxwYXRoIGQ9Ik01LjI3Njk5IDE0LjI2OEM1LjAzMjM1IDEzLjUzNjkgNC45MDgwNiAxMi43NzA5IDQuOTA4OTkgMTJDNC45MDg5OSAxMS4yMTggNS4wMzM5OSAxMC40NjcgNS4yNjU5OSA5Ljc2NUwxLjIzOTk5IDYuNjVDMC40MTYzNzIgOC4zMTI2OSAtMC4wMDgyMDIwMSAxMC4xNDQ1IC01LjYxNTc3ZS0wNiAxMkMtNS42MTU3N2UtMDYgMTMuOTIgMC40NDQ5OTQgMTUuNzMgMS4yMzY5OSAxNy4zMzVMNS4yNzY5OSAxNC4yNjhaIiBmaWxsPSIjRkJCQzA1Ii8+CjwvZz4KPGRlZnM+CjxjbGlwUGF0aCBpZD0iY2xpcDAiPgo8cmVjdCB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIGZpbGw9IndoaXRlIi8+CjwvY2xpcFBhdGg+CjwvZGVmcz4KPC9zdmc+Cg==')",
        },
    },
};
